import {
  useEffect, useState, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'utils/cn';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { scrollTop } from 'utils/scroll-top';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { SummarySection } from 'features/Croptimizer/components/SummarySection/SummarySection';
import { Country } from 'utils/types';

import FiveYearsBoardLoss from './components/FiveYearsBoardLoss';
import FiveYearsBoardProfit from './components/FiveYearsBoardProfit';

import { useGermanStatusBoard } from '../1Board/components/BoardSteps/components/1Status/German/hooks/useGermanStatusBoard';
import { useStatusBoard } from '../1Board/components/BoardSteps/components/1Status/Standart/hooks/useStatusBoard';

interface Props {
  isPDF: boolean;
}

const FiveYearsBoard = ({ isPDF }: Props) => {
  const { setShowBack, setBackHandler, setShowNext, setBackDisabled } =
    useNextHandler();
  const { t } = useTranslation();

  const { setActiveTab, user } = useCroptimizer();
  const isGerman = user?.country?.name === Country.Germany;

  const [profitTitle, setProfitTitle] = useState<string>('');
  const [lossTitle, setLossTitle] = useState<string>('');
  const {
    errorCurrent,
    errorTarget,
    errorDose,
    isGreaterTarget,
  } = isGerman ? useGermanStatusBoard() : useStatusBoard();
  
  const { boardStatusValues } = useCroptimizer();
  const isMaintenanceMode = useMemo(
    () => Number(boardStatusValues?.currentPh) === Number(boardStatusValues?.targetPh),
    [boardStatusValues?.currentPh, boardStatusValues?.targetPh],
  );
  
  const hasErrors = errorCurrent.length > 0 || errorTarget.length > 0 || (!!errorDose && errorDose?.length > 0);
  
  useEffect(() => {
    // Configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling

    setBackDisabled(hasErrors);
    setShowBack(true);
    setShowNext(false);
    setBackHandler(() => {
      setActiveTab(1);
      scrollTop();
    });

    // INFO
    // CrOptimizer-Simplification task
    // Hide the following pages: Crop Rotation, Supply Plan, Synthesis

    /*     setHandler(() => {
      setActiveTab(3);
      scrollTop();
    }); */
  }, [errorCurrent, errorTarget, errorDose]);
  
  return (
    <div>
      <SummarySection isPDF={isPDF} />
      <div
        className={cn({
          'border border-primitiveGrey p-8 md:py-3 md:px-4 rounded-2xl': !isPDF,
        })}
      >
        <div className="text-black font-bold text-xl md:text-lg md:mt-6 mb-4">
          {t('whatIsStrategyFiveYears')}
        </div>
        <div className="text-black text-xl md:text-lg md:mt-6 mb-4">
          {t('fiveYearsDisclaimer')}
        </div>
      
        <FiveYearsBoardProfit
          profitTitle={profitTitle}
          setLossTitle={setLossTitle}
          hasErrors={hasErrors}
          isMaintenanceMode={isMaintenanceMode}
          isGreaterTarget={isGreaterTarget}
        />

        <FiveYearsBoardLoss
          setProfitTitle={setProfitTitle}
          lossTitle={lossTitle}
          hasErrors={hasErrors}
        />
      </div>
    </div>
  );
};

export default FiveYearsBoard;
